import React, { useState } from 'react';
import { Row, Col, Typography, Tabs, Button } from 'antd';
import styled, { keyframes } from 'styled-components';
import { UserOutlined, DashboardOutlined, ArrowRightOutlined } from '@ant-design/icons';
import ProfileDemo from './ProfileDemo';
import DashboardDemo from './DashboardDemo';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled Components
const DemoContainer = styled.section`
  width: 100%;
  max-width: 100vw;
  padding: ${props => props.theme.spacing?.[24] || '6rem'} 
    ${props => props.theme.spacing?.[6] || '1.5rem'} 
    ${props => props.theme.spacing?.[20] || '5rem'};
  background: ${props => props.theme.colors?.neutral?.[900] || '#171717'};
  position: relative;
  overflow: hidden;
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[16] || '4rem'} 
      ${props => props.theme.spacing?.[4] || '1rem'} 
      ${props => props.theme.spacing?.[12] || '3rem'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 30%, rgba(226, 39, 132, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing?.[16] || '4rem'};
  animation: ${fadeInUp} 1s ease-out;
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
    }
  }
`;

const SectionSubtitle = styled(Paragraph)`
  && {
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    max-width: 600px;
    margin: 0 auto ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const DemoTabsContainer = styled.div`
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['2xl'] || '1rem'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: ${props => props.theme.spacing?.[8] || '2rem'};
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  
  .ant-tabs {
    .ant-tabs-tab {
      color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
      font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
      font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
      
      &.ant-tabs-tab-active {
        .ant-tabs-tab-btn {
          color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
        }
      }
      
      &:hover {
        .ant-tabs-tab-btn {
          color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
        }
      }
    }
    
    .ant-tabs-ink-bar {
      background: ${props => props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
      height: 3px;
    }
    
    .ant-tabs-content-holder {
      .ant-tabs-content {
        .ant-tabs-tabpane {
          padding-top: ${props => props.theme.spacing?.[6] || '1.5rem'};
        }
      }
    }
  }
`;

const DemoContent = styled.div`
  min-height: 600px;
  border-radius: ${props => props.theme.borderRadius?.xl || '0.75rem'};
  overflow: hidden;
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing?.[12] || '3rem'};
  animation: ${fadeInUp} 1s ease-out 0.4s both;
`;

const CTAButton = styled(Button)`
  && {
    background: ${props => props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
    border: none;
    border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
    height: 56px;
    padding: 0 ${props => props.theme.spacing?.[8] || '2rem'};
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    color: white;
    display: inline-flex;
    align-items: center;
    gap: ${props => props.theme.spacing?.[2] || '0.5rem'};
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'} 
      ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};
    
    &:hover, &:focus {
      color: white;
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.boxShadow?.xl || '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
      animation: ${pulse} 1s ease-in-out;
    }
    
    .anticon {
      font-size: 1.25rem;
    }
  }
`;

const DemoSection = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const handleJoinWaitlist = () => {
    // Scroll to waitlist section
    const waitlistSection = document.getElementById('waitlist-section');
    if (waitlistSection) {
      waitlistSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <DemoContainer id="demo-section">
      <ContentWrapper>
        <SectionHeader>
          <SectionTitle level={2}>Experience CLLCTVE in Action</SectionTitle>
          <SectionSubtitle>
            See how our platform empowers creatives to build their portfolios, connect with brands, 
            and grow their careers. Explore the profile and dashboard features below.
          </SectionSubtitle>
        </SectionHeader>

        <DemoTabsContainer>
          <Tabs 
            activeKey={activeTab} 
            onChange={handleTabChange}
            size="large"
            centered
          >
            <TabPane
              tab={
                <span>
                  <UserOutlined />
                  Creative Profile
                </span>
              }
              key="profile"
            >
              <DemoContent>
                <ProfileDemo />
              </DemoContent>
            </TabPane>
            
            <TabPane
              tab={
                <span>
                  <DashboardOutlined />
                  Dashboard
                </span>
              }
              key="dashboard"
            >
              <DemoContent>
                <DashboardDemo />
              </DemoContent>
            </TabPane>
          </Tabs>
        </DemoTabsContainer>

        <CTASection>
          <CTAButton 
            size="large" 
            onClick={handleJoinWaitlist}
            icon={<ArrowRightOutlined />}
          >
            Join the Waitlist to Get Started
          </CTAButton>
        </CTASection>
      </ContentWrapper>
    </DemoContainer>
  );
};

export default DemoSection;
